import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard } from "../components/common";
import { ProductCombobox } from "../components/ProductCombobox";
import { DepotField } from "../components/DepotField";
import { MovementTable } from "../components/MovementTable";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError, qtyFmt } from "../lib/api";

function TransferForm({ products, onDone }) {
  const { user, isAdmin } = useAuth();
  const { depotParam, depots } = useDepot();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) setFrom(user.depot_id);
    else if (depotParam) setFrom(depotParam);
    else if (!from && depots.length) setFrom(depots[0].id);
  }, [isAdmin, user, depotParam, depots, from]);

  const submit = async (e) => {
    e.preventDefault();
    if (!product || !from || !to) { toast.error("Select product, source and destination"); return; }
    setBusy(true);
    try {
      await api.post("/transfers", { product_id: product.id, from_depot_id: from, to_depot_id: to, qty: Number(qty), note });
      toast.success(`Transferred ${qty} ${product.unit} of ${product.name}`);
      setProduct(null); setQty(""); setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 p-5" data-testid="transfer-form">
      <div className="grid grid-cols-2 gap-4">
        <DepotField label="From depot" value={from} onChange={(v) => { setFrom(v); if (v === to) setTo(""); }} testid="transfer-from-select" />
        <DepotField label="To depot" value={to} onChange={setTo} exclude={from} alwaysSelect testid="transfer-to-select" />
      </div>
      <div className="space-y-1.5">
        <Label className="label-mono">Product</Label>
        <ProductCombobox products={products} value={product} onChange={setProduct} depotId={from} testid="transfer-product" />
        {product && <div className="text-xs text-slate-400">Available at source: <span className="font-mono text-slate-200">{qtyFmt(product.stock?.[from] ?? 0)} {product.unit}</span></div>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="label-mono">Quantity</Label>
          <Input type="number" step="any" min="0" required value={qty} onChange={(e) => setQty(e.target.value)} className="bg-slate-950" data-testid="transfer-qty-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="label-mono">Note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="bg-slate-950" data-testid="transfer-note-input" />
        </div>
      </div>
      <Button type="submit" disabled={busy} className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="transfer-submit-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Transfer stock
      </Button>
    </form>
  );
}

export default function Transfers() {
  const { isAdmin } = useAuth();
  const { depotParam, scopeLabel } = useDepot();
  const products = useApi("/products", { depot_id: depotParam });
  const moves = useApi("/movements", { type: "transfer", depot_id: depotParam, limit: 50 });
  const refresh = () => { products.reload(); moves.reload(); };
  const voidMove = async (id) => {
    try { await api.delete(`/movements/${id}`); toast.success("Transfer reversed"); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="Depot Transfers" subtitle="Move stock between depots. No cash is recorded — only quantities move." />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <SectionCard title="New transfer" className="xl:col-span-5 self-start">
          <TransferForm products={products.data || []} onDone={refresh} />
        </SectionCard>
        <SectionCard title="Recent transfers" className="xl:col-span-7">
          <MovementTable rows={moves.data} onVoid={isAdmin ? voidMove : null} testid="transfers-table" />
        </SectionCard>
      </div>
    </div>
  );
}
